import React, { useState } from 'react';
import styled from 'styled-components';
import Loader from '../Components/Loader';
import SuccessCard from '../Components/SuccessCard';
import ErrorCard from '../Components/ErrorCard';
import useIAList from '../Hooks/CisHooks/useIAListBohol';
import useCISBoholLabels from '../Hooks/CisHooks/useCISBoholLabels';
import useAddCISBohol from '../Hooks/CisHooks/useAddCISBohol';
import MunicipalitySelector from '../Components/Municipality';
import MailingAddressSelector from '../Components/MailingAddressSelector';

const CISboholAdd = ({ darkMode, onClose, onRefresh, onAddSuccess  }) => {
  const { loading, success, error, addCISBohol } = useAddCISBohol();
  const { iaList, loading: iaLoading, error: iaError } = useIAList();
  const { labels, loading: labelsLoading, error: labelsError } = useCISBoholLabels();
  const [showSuccess, setShowSuccess] = useState(false);
  const [addSuccessMessage, setAddSuccessMessage] = useState(null);
  const [showError, setShowError] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // ✅ Updated initialFormData (matches backend route)
const initialFormData = {
  years: '',
  label: '',
  ia_name: '',
  num: '',
  cis_name: '',
  districtNum: '',
  municipality: '',
  mailing_address: '',
  president: '',
  contactNum: '',
  email_address: '',
  dateOrganized: '',
  sec_RegistrationNum: '',
  sec_DateApproved: '',
  tin: '',
  water_permitNum: '',
  water_dateApproved: '',
  membership_fb: '',
  membership_actual: '',
  sectorNum: '',
  serviceArea: '',
  fusa: '',
  operational: '',
  date_moaSigned: '',
  f_source: '',
  projCost: '',
  date_turnOver: '',
  ia_existContract: '',
  imt_dateSigned: '',
  imt_undercontract: '',
  canalLength_lined: '',
  canalLength_unlined: '',
  total_CanalLength: '',
  fundSource: '',
  category: '',
  devolutionPhases: '',
  idoAssigned: '',
  remarks: ''
};

  const [formData, setFormData] = useState(initialFormData);
  const [isExpanded] = useState(true);

  const formSections = [
    { title: null, fields: ['years', 'label', 'ia_name', 'num', 'cis_name'] },
    { title: null, fields: ['districtNum', 'municipality', 'mailing_address', 'president', 'contactNum', 'email_address'] },
    { title: 'Registration', fields: ['dateOrganized', 'sec_RegistrationNum', 'sec_DateApproved', 'tin'] },
    { title: null, fields: ['water_permitNum', 'water_dateApproved'] },
    { title: null, fields: ['membership_fb', 'membership_actual', 'sectorNum'] },
    { title: 'Area Details', fields: ['serviceArea', 'fusa', 'operational'] },
    { title: null, fields: ['date_moaSigned', 'f_source', 'projCost', 'date_turnOver', 'ia_existContract'] },
    { title: 'IMT Contact', fields: ['imt_dateSigned', 'imt_undercontract'] },
    { title: null, fields: ['canalLength_lined', 'canalLength_unlined', 'total_CanalLength'] },
    { title: null, fields: ['fundSource', 'category', 'devolutionPhases', 'idoAssigned', 'remarks'] }
  ];
  
  const fieldLabelMap = {
    years: 'Year',
    label: 'Label',
    ia_name: 'IA Name',
    num: 'IA Number',
    cis_name: 'CIS Name',
    districtNum: 'District Number',
    municipality: 'Municipality',
    mailing_address: 'Mailing Address',
    president: 'President',
    contactNum: 'Contact Number',
    email_address: 'Email Address',
    dateOrganized: 'Date Organized',
    sec_RegistrationNum: 'SEC Registration Number',
    sec_DateApproved: 'SEC Date Approved',
    tin: 'TIN',
    water_permitNum: 'Water Permit Number',
    water_dateApproved: 'Water Date Approved',
    membership_fb: 'Membership (FB)',
    membership_actual: 'Membership (Actual)',
    sectorNum: 'Sector Number',
    serviceArea: 'Service Area',
    fusa: 'FUSA',
    operational: 'Operational Area',
    date_moaSigned: 'Date MOA Signed',
    f_source: 'Funding Source',
    projCost: 'Project Cost',
    date_turnOver: 'Date Turn Over',
    ia_existContract: 'IA Existing Contract',
    imt_dateSigned: 'IMT Date Signed',
    imt_undercontract: 'IMT Under Contract',
    canalLength_lined: 'Canal Length (Lined)',
    canalLength_unlined: 'Canal Length (Unlined)',
    total_CanalLength: 'Total Canal Length',
    fundSource: 'Fund Source',
    category: 'Category',
    devolutionPhases: 'Devolution Phases',
    idoAssigned: 'IDO Assigned',
    remarks: 'Remarks'
  };
  

  const validateField = (name, value) => {
    if (!value || value.trim() === '') return false;
  
    switch (name) {
      case 'years':
        return /^\d{4}$/.test(value);
      case 'contactNum':
        return /^\d{7,15}$/.test(value);
      case 'email_address':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'projCost':
        return !isNaN(value) && Number(value) >= 0;
      case 'serviceArea':
      case 'fusa':
      case 'operational':
      case 'membership_fb':
      case 'membership_actual':
      case 'canalLength_lined':
      case 'canalLength_unlined':
      case 'total_CanalLength':
        return !isNaN(value) && Number(value) > 0;
      default:
        return true;
    }
  };
  

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((field) => {
      if (
        (field === 'custom_is_name' && formData.is_name !== 'Others ...') ||
        (field === 'custom_ia_name' && formData.ia_name !== 'Others ...')
      ) {
        return;
      }
      if (!validateField(field, formData[field])) {
        errors[field] = true;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name] && validateField(name, value)) {
      setValidationErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) {
    setShowError(true);
    return;
  }
  const submissionData = {
    ...formData,
    label: formData.label === 'Others ...' ? formData.custom_label.toUpperCase() : formData.label,
    is_name: formData.is_name === 'Others ...' ? formData.custom_is_name.toUpperCase() : formData.is_name,
    ia_name: formData.ia_name === 'Others ...' ? formData.custom_ia_name.toUpperCase() : formData.ia_name,
  };

  const result = await addCISBohol(submissionData);

  if (result.success) {
    // Call the parent's onAddSuccess to display the message
    if (onAddSuccess) {
      onAddSuccess('CIS Bohol record successfully added!');
    }
    // Reset form
    setFormData(initialFormData);
    setValidationErrors({});
    setShowError(false);
    // Trigger refetch in parent
    if (onRefresh) onRefresh();
    // Close the panel
    onClose();
  } else {
    setShowError(true);
  }
};


  const handleClose = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    setShowSuccess(false);
    setShowError(false);
    onClose();
  };

  return (
    <Container darkMode={darkMode} isExpanded={isExpanded}>
      <FormContainer darkMode={darkMode} isExpanded={isExpanded}>
        <Title darkMode={darkMode}>
          <span>CIS Form</span>
          <CloseButton onClick={handleClose} darkMode={darkMode}>
            X
          </CloseButton>
        </Title>
        {showError && Object.keys(validationErrors).length > 0 && (
          <ErrorCard
            errorMessage="Please fill out all fields correctly."
            onClose={() => setShowError(false)}
          />
        )}

        {(loading || iaLoading || labelsLoading) && (
  <LoaderOverlay>
    <Loader />
  </LoaderOverlay>
)}


{(error || iaError || labelsError) && (
  <ErrorCard
    errorMessage={error || iaError || labelsError || 'An error occurred while fetching data.'}
    onClose={() => setShowError(false)}
  />
)}



        <form onSubmit={handleSubmit}>
          {formSections.map((section, index) => (
            <CardSection darkMode={darkMode} key={index}>
              {section.title && (
                <SectionHeader darkMode={darkMode}>{section.title}</SectionHeader>
              )}
              {section.fields.map((key) => (
                <FieldGroup key={key} darkMode={darkMode} hasError={validationErrors[key]}>
                  <Label htmlFor={key} hasError={validationErrors[key]}>
                    {fieldLabelMap[key]}
                    <RequiredAsterisk>*</RequiredAsterisk>
                  </Label>
                  {key === 'years' ? (
                    <Input
                      type="text"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder="Enter Year (e.g., 2023)"
                      aria-label={fieldLabelMap[key]}
                      maxLength="4"
                      hasError={validationErrors[key]}
                    />
                  ) : key === 'label' ? (
  <>
    <Select
      id={key}
      name={key}
      value={formData[key]}
      onChange={handleChange}
      aria-label={fieldLabelMap[key]}
      hasError={validationErrors[key]}
    >
      <option value="" disabled>Select Label</option>
      {labels.map((label, idx) => (
        <option key={idx} value={label.label}>
          {label.label}
        </option>
      ))}
      <option value="Others ...">Add new Label</option>
    </Select>

    {formData.label === 'Others ...' && (
      <Input
        type="text"
        id="custom_label"
        name="custom_label"
        value={formData.custom_label || ''}
        onChange={handleChange}
        placeholder="Enter Custom Label"
        aria-label="Custom Label"
        hasError={validationErrors.custom_label}
        style={{ marginTop: '8px' }}
      />
    )}
  </>

                  ) : key === 'ia_name' ? (
                    <>
                      <Select
                        id={key}
                        name={key}
                        value={formData[key]}
                        onChange={handleChange}
                        aria-label={fieldLabelMap[key]}
                        hasError={validationErrors[key]}
                      >
                        <option value="" disabled>Select IA Name</option>
                        {iaList.map((ia) => (
                          <option key={ia.ia_id || ia.id} value={ia.ia_name}>
                            {ia.ia_name}
                          </option>
                        ))}
                        <option value="Others ...">Add new IA</option>
                      </Select>
                      {formData.ia_name === 'Others ...' && (
                        <Input
                          type="text"
                          id="custom_ia_name"
                          name="custom_ia_name"
                          value={formData.custom_ia_name}
                          onChange={handleChange}
                          placeholder="Enter Custom IA Name"
                          aria-label={fieldLabelMap.custom_ia_name}
                          hasError={validationErrors.custom_ia_name}
                          style={{ marginTop: '8px' }}
                        />
                      )}
                    </>
                  ) : key === 'municipality' ? (
                    <MunicipalitySelector
                      formData={formData}
                      setFormData={setFormData}
                      darkMode={darkMode}
                      hasError={validationErrors[key]}
                    />
                  ) : key === 'mailing_address' ? (
                    <MailingAddressSelector
                      formData={formData}
                      setFormData={setFormData}
                      darkMode={darkMode}
                      hasError={validationErrors[key]}
                    />
                 ) : ['dateOrganized','sec_DateApproved','water_dateApproved','date_moaSigned','date_turnOver','imt_dateSigned'].includes(key) ? (
                    <Input
                      type="date"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      aria-label={fieldLabelMap[key]}
                      hasError={validationErrors[key]}
                    />
                  ) : (
                    <Input
                      type="text"
                      id={key}
                      name={key}
                      value={formData[key]}
                      onChange={handleChange}
                      placeholder={
                        key === 'contactNum'
                          ? 'Enter Contact Number (7-15 digits)'
                          : key === 'ia_tin'
                          ? 'Enter TIN (alphanumeric, up to 20 chars)'
                          : fieldLabelMap[key]
                      }
                      aria-label={fieldLabelMap[key]}
                      maxLength={key === 'contactNum' ? '15' : key === 'ia_tin' ? '20' : '100'}
                      hasError={validationErrors[key]}
                    />
                  )}
                </FieldGroup>
              ))}
            </CardSection>
          ))}
          <FormButtonContainer>
            <SubmitButton darkMode={darkMode} type="submit">
              Add Information
            </SubmitButton>
          </FormButtonContainer>
        </form>
      </FormContainer>
    </Container>
  );
};

export default CISboholAdd;

const Container = styled.div`
  padding: 20px;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  position: relative;
  transition: transform 0.3s ease;
  transform: ${({ isExpanded }) => (isExpanded ? 'translateX(0%)' : 'translateX(80%)')};
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
  background-image: ${({ darkMode }) => (darkMode ? 'url(/path/to/adminBg.jpg)' : 'none')};
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5px;
  flex-grow: 1;
  overflow-y: auto;
  transition: width 0.3s ease, transform 0.3s ease;
  width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 200px)')};

  @media (max-width: 1024px) {
    width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 150px)')};
  }

  @media (max-width: 768px) {
    width: 100%;
    transform: translateX(0);
  }
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 5px;
  margin: 0 0 10px 0;
  font-size: 22px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.45)' : '#f2f2f2')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#ccc')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    flex: 1;
    text-align: center;
  }
`;

const LoaderOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
`;

const CardSection = styled.div`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  padding: 5px;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: ${({ darkMode }) => (darkMode ? '0 3px 8px rgba(255, 255, 255, 0.2)' : '0 3px 10px rgba(0, 0, 0, 0.1)')};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionHeader = styled.h3`
  grid-column: span 2;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
  border-bottom: 1px solid ${({ darkMode }) => (darkMode ? '#ccc' : '#ddd')};
  padding-bottom: 5px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 5px;
  color: ${({ darkMode, hasError }) => (hasError ? '#ff0000' : darkMode ? '#f2f2f2' : '#333')};
`;

const RequiredAsterisk = styled.span`
  color: #ff0000;
  margin-left: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: #f1f1f1;
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ff0000' : '#4caf50')};
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ff0000' : '#388e3c')};
  }

  &:hover {
    background-color: #f1f1f1;
  }

  option {
    color: #333;
    background-color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin: 10px 0;
  padding-top: 20px;
  padding-bottom: 60px;
`;

const SubmitButton = styled.button`
  padding: 8px 16px;
  font-size: 14px;
  font-weight: bold;
  background: ${({ darkMode }) => (darkMode ? '#4caf50' : '#18591d')};
  color: #fff;
  border: 1px solid ${({ darkMode }) => (darkMode ? '#388e3c' : '#144d17')};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ darkMode }) => (darkMode ? '#388e3c' : '#218838')};
  }
`;

const CloseButton = styled.button`
  background Alone: none;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  cursor: pointer;
  padding: 5px 10px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ darkMode }) => (darkMode ? '#ff5555' : '#cc0000')};
  }
`;